/**
 * One order over type names, for everything that groups by type.
 *
 * The legend's swatches, the categorical color slots and the bands on the bundled sphere all sort
 * the same names, and two of them disagreeing is visible: a legend whose order has nothing to do
 * with the order the bands appear in reads as a legend for a different picture.
 *
 * Count descending, so the commonest type takes the first color slot and the widest band.
 */

import type { NetworkNode } from '../types';

/**
 * Deterministic, locale-independent name order.
 *
 * Codepoint order rather than `localeCompare`, and that is the point rather than a way around the
 * lint rule: a locale-aware comparison would order two type names differently for a German reader
 * than for an English one, so the same estate would come out with its colors and its bands swapped
 * depending on who opened it. Ties here exist only to be broken reproducibly.
 */
const compareTypeNames = (left: string, right: string): number =>
  left < right ? -1 : left > right ? 1 : 0;

const toNodeCountByType = (
  nodes: readonly NetworkNode[]
): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const { typeName } of nodes) {
    counts.set(typeName, (counts.get(typeName) ?? 0) + 1);
  }
  return counts;
};

/** Type names, commonest first, ties broken on name. */
export const toTypesByFrequency = (nodes: readonly NetworkNode[]): string[] =>
  [...toNodeCountByType(nodes).entries()]
    .sort(
      ([leftName, leftCount], [rightName, rightCount]) =>
        rightCount - leftCount || compareTypeNames(leftName, rightName)
    )
    .map(([typeName]) => typeName);
